/**
 * TemplateCatalogDialog — add a built-in process to this organization, on demand.
 *
 * The catalog exists because seeding built-ins as live definitions gave every tenant three
 * processes it never asked for, could not delete, and could only archive (owner, 2026-08-23).
 * They are now `is_template=true` rows; adding one clones it into an editable DRAFT and
 * nothing is published until the admin publishes it.
 *
 * The clone is a SERVER call (`use-template`): the rules — source must be a template in my
 * org, copy is a draft, seed provenance stripped — belong next to the data, not in the client.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountTree as FlowIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { ApiError, processesApi } from '../../../../api';
import type { ProcessDefinition } from '../../../../api/processes';
import { translateApiError } from '../../../../i18n/translateApiError';
import { getProcessDefinitionDisplay } from '../../processDisplay';

interface TemplateCatalogDialogProps {
  open: boolean;
  onClose: () => void;
  onAdded: (draftId: string) => void;
}

const TemplateCatalogDialog: React.FC<TemplateCatalogDialogProps> = ({ open, onClose, onAdded }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<ProcessDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    processesApi.processDefinitionsApi.list()
      .then((definitions) => setTemplates(definitions.filter((definition) => definition.is_template)))
      .catch((caught) => setError(
        caught instanceof ApiError ? translateApiError(caught, t) : t('common:feedback.loadFailed'),
      ))
      .finally(() => setLoading(false));
  }, [open, t]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((template) => {
      const display = getProcessDefinitionDisplay(t, template);
      return [template.name, template.description ?? '', display.name, display.description]
        .some((value) => value.toLowerCase().includes(query));
    });
  }, [templates, search, t]);

  const add = async (template: ProcessDefinition) => {
    setAddingId(template.id);
    setError(null);
    try {
      const draft = await processesApi.processDefinitionsApi.useTemplate(template.id);
      onAdded(draft.id);
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? translateApiError(caught, t)
          : t('processes:templateCatalog.addFailed'),
      );
    } finally {
      setAddingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ m: 0, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('processes:templateCatalog.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('processes:templateCatalog.subtitle')}
          </Typography>
        </Box>
        <IconButton aria-label={t('common:actions.close')} onClick={onClose} disabled={!!addingId}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <TextField
          size="small"
          fullWidth
          placeholder={t('processes:templateCatalog.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> }}
          sx={{ mb: 2 }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={24} /></Box>
        ) : filtered.length === 0 ? (
          <Alert severity="info">{t('processes:templateCatalog.empty')}</Alert>
        ) : (
          <Stack spacing={1.25}>
            {filtered.map((template) => {
              const display = getProcessDefinitionDisplay(t, template);
              const nodeCount = (template.flow as { nodes?: unknown[] } | null)?.nodes?.length ?? 0;
              return (
                <Card key={template.id} variant="outlined" sx={{ '&:hover': { borderColor: 'primary.main' } }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <FlowIcon color="primary" fontSize="small" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{display.name}</Typography>
                        {display.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {display.description}
                          </Typography>
                        )}
                        <Chip
                          label={t('processes:processDesigner.nodes', { count: nodeCount })}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.75, height: 18, fontSize: '0.65rem', borderRadius: 1 }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!!addingId}
                        onClick={() => add(template)}
                        sx={{ textTransform: 'none', minWidth: 92 }}
                      >
                        {addingId === template.id
                          ? <CircularProgress size={16} color="inherit" />
                          : t('processes:templateCatalog.add')}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCatalogDialog;
